import { type ReactElement, type ReactNode, useMemo } from 'react';
import type { Row } from '@tanstack/react-table';

import { cn } from '@crossnokaye/ux-common';

import { useTable } from '@/components/data-table/core/context';

// =============================================================================
// Types
// =============================================================================

export interface BulkSelectBarProps<TData = unknown> {
  /** Render function that receives selected rows */
  children: (selections: Row<TData>[]) => ReactNode;
  /** Additional CSS classes for the bar container */
  className?: string;
  /** Always show the bar, even when no rows are selected (default: false) */
  alwaysVisible?: boolean;
}

// =============================================================================
// Component
// =============================================================================

/**
 * BulkSelectBar - A bar that appears when rows are selected.
 *
 * Renders above the table when one or more rows are selected.
 * Accepts a render prop that receives the array of selected rows.
 *
 * @example
 * ```tsx
 * <DataTable id="users" data={users} getRowId={(row) => row.id}>
 *   <BulkSelectBar>
 *     {(selections) => (
 *       <div className="flex items-center gap-2">
 *         <span>{selections.length} selected</span>
 *         <Button onClick={() => handleDelete(selections)}>Delete</Button>
 *       </div>
 *     )}
 *   </BulkSelectBar>
 *   <SelectColumn />
 *   {columns...}
 * </DataTable>
 * ```
 */
export const BulkSelectBar = <TData,>({
  children,
  className,
  alwaysVisible = false,
}: BulkSelectBarProps<TData>): ReactElement | null => {
  const { table, rowSelection } = useTable<TData>();

  // Get selected rows from the table
  const selectedRows = useMemo(() => {
    return table.getRowModel().rows.filter((row) => rowSelection[row.id]);
  }, [table, rowSelection]);

  // Hide when nothing selected (unless alwaysVisible is true)
  if (!alwaysVisible && selectedRows.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'bulk-select-bar',
        'flex items-center gap-4 px-4 py-2',
        'bg-muted/50 border-b border-border',
        className
      )}
      data-component="bulk-select-bar"
      data-selected-count={selectedRows.length}
    >
      {children(selectedRows)}
    </div>
  );
};

BulkSelectBar.displayName = 'DataTable.BulkSelectBar';
